import {
  ApiError,
  type AccountRole,
  type SessionState,
  type SystemEntry,
  actingRole,
  api,
  loadSession,
  setActingRole,
  setCsrfToken,
} from "@nlrm/api-client";
import {
  EnvironmentOutlined,
  CloudServerOutlined,
  LockOutlined,
  LogoutOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {useEffect, useMemo, useState} from "react";

const icons = {
  reimbursement: <WalletOutlined />,
  attendance: <EnvironmentOutlined />,
  "cloud-drive": <CloudServerOutlined />,
  settings: <SettingOutlined />,
};

export type AuthMode = "login" | "setup" | "reset";
export type AccountState = "setup_required" | "password_ready" | "unknown";

export function availableAuthModes(accountState: AccountState | null): Array<{label: string; value: AuthMode}> {
  const options: Array<{label: string; value: AuthMode}> = [{label: "密码登录", value: "login"}];
  if (accountState === "setup_required") options.push({label: "首次设置", value: "setup"});
  if (accountState === "password_ready") options.push({label: "忘记密码", value: "reset"});
  return options;
}

function AuthPage({onAuthenticated}: {onAuthenticated: (value: SessionState) => void}) {
  const [mode, setMode] = useState<AuthMode>("login");
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();
  const [notice, setNotice] = useState("");
  const [accountState, setAccountState] = useState<AccountState | null>(null);
  const [checkingAccount, setCheckingAccount] = useState(false);
  const authOptions = useMemo(() => availableAuthModes(accountState), [accountState]);

  useEffect(() => {
    if (!authOptions.some((item) => item.value === mode)) setMode("login");
  }, [authOptions, mode]);

  async function checkAccountState() {
    try {
      await form.validateFields(["email"]);
      setCheckingAccount(true);
      const result = await api<{state: AccountState}>("/api/v1/auth/account-state", {
        method: "POST",
        body: JSON.stringify({email: form.getFieldValue("email")}),
      });
      setAccountState(result.state);
    } catch (error) {
      if (error instanceof ApiError) message.error(error.message);
      setAccountState(null);
    } finally {
      setCheckingAccount(false);
    }
  }

  async function sendCode() {
    try {
      const email = form.getFieldValue("email");
      if (!email) throw new Error("请先填写邮箱。")
      setBusy(true);
      const result = await api<{message: string}>("/api/v1/auth/password-code", {
        method: "POST",
        body: JSON.stringify({email, purpose: mode}),
      });
      setNotice(result.message);
    } catch (error) {
      message.error(error instanceof Error ? error.message : "验证码发送失败。");
    } finally {
      setBusy(false);
    }
  }

  async function submit(values: Record<string, string>) {
    setBusy(true);
    try {
      if (mode === "login") {
        const result = await api<SessionState>("/api/v1/auth/login", {
          method: "POST",
          body: JSON.stringify({email: values.email, password: values.password}),
        });
        setCsrfToken(result.csrfToken);
        onAuthenticated(result);
        return;
      }
      if (values.password !== values.confirmPassword) throw new Error("两次输入的密码不一致。")
      await api("/api/v1/auth/password", {
        method: "POST",
        body: JSON.stringify({email: values.email, purpose: mode, code: values.code, password: values.password}),
      });
      message.success("密码已设置，请登录。")
      setAccountState("password_ready");
      form.resetFields(["code", "password", "confirmPassword"]);
      setMode("login");
    } catch (error) {
      message.error(error instanceof Error ? error.message : "操作失败。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-story">
        <div className="story-orbit orbit-one" />
        <div className="story-orbit orbit-two" />
        <div className="auth-brand"><img src="/assets/favicon.png" alt="NLRM" /><span>NLRM</span></div>
        <div className="story-copy">
          <Tag color="blue">NLRM SERVICE HUB</Tag>
          <h1>让课题组协作，<br />从一个清晰入口开始。</h1>
          <p>统一身份、统一体验、独立系统边界。服务平台负责连接，子系统专注完成工作。</p>
        </div>
        <div className="story-trust">
          <SafetyCertificateOutlined />
          <span>HTTPS 加密 · 独立数据边界 · 权限按身份执行</span>
        </div>
      </section>
      <section className="auth-form-side">
        <Card className="auth-card" bordered={false}>
          <div className="auth-card-title">
            <Avatar size={48} src="/assets/favicon.png" />
            <div><Typography.Title level={2}>欢迎使用 NLRM 综合服务平台</Typography.Title><Typography.Text type="secondary">使用已登记邮箱账号进入平台</Typography.Text></div>
          </div>
          <Form form={form} layout="vertical" onFinish={(values) => void submit(values)} requiredMark="optional">
            <Form.Item label="邮箱账号" name="email" rules={[{required: true}, {type: "email"}]}>
              <Input
                size="large"
                prefix={<MailOutlined />}
                placeholder="name@example.com"
                autoComplete="email"
                onChange={() => {setAccountState(null); setNotice("");}}
                onBlur={() => void checkAccountState()}
                suffix={checkingAccount ? <Spin size="small" /> : undefined}
              />
            </Form.Item>
          <Segmented<AuthMode>
            block
            value={mode}
            onChange={(value) => {setMode(value); setNotice(""); form.resetFields(["code", "password", "confirmPassword"]);}}
            options={authOptions}
          />
          {notice && <Alert type="success" showIcon message={notice} />}
            {mode !== "login" && (
              <Form.Item label="邮箱验证码" required>
                <Space.Compact block>
                  <Form.Item noStyle name="code" rules={[{required: true}, {len: 6}]}>
                    <Input size="large" inputMode="numeric" maxLength={6} placeholder="6 位数字" />
                  </Form.Item>
                  <Button size="large" onClick={() => void sendCode()} loading={busy}>发送验证码</Button>
                </Space.Compact>
              </Form.Item>
            )}
            <Form.Item label={mode === "login" ? "密码" : "设置密码"} name="password" rules={[{required: true}, {min: mode === "login" ? 1 : 12, max: 128}]}>
              <Input.Password size="large" prefix={<LockOutlined />} autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder={mode === "login" ? "输入密码" : "至少 12 个字符"} />
            </Form.Item>
            {mode !== "login" && (
              <Form.Item label="确认密码" name="confirmPassword" rules={[{required: true}]}>
                <Input.Password size="large" prefix={<LockOutlined />} autoComplete="new-password" />
              </Form.Item>
            )}
            <Button type="primary" htmlType="submit" size="large" block loading={busy}>
              {mode === "login" ? "登录 NLRM 综合服务平台" : mode === "setup" ? "完成首次设置" : "重置密码"}
            </Button>
          </Form>
        </Card>
      </section>
    </main>
  );
}

function Portal({session}: {session: SessionState}) {
  const queryClient = useQueryClient();
  const [role, setRole] = useState<AccountRole>(actingRole(session.user));
  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("全部");
  const catalog = useQuery({
    queryKey: ["catalog", role],
    queryFn: () => api<{systems: SystemEntry[]}>("/api/v1/catalog", {}, session.user),
  });
  const systems = catalog.data?.systems || [];
  const categories = ["全部", ...new Set(systems.map((item) => item.category))];
  const filtered = useMemo(
    () => systems.filter((item) =>
      (category === "全部" || item.category === category)
      && `${item.title}${item.description}${item.category}`.toLowerCase().includes(keyword.toLowerCase()),
    ),
    [systems, category, keyword],
  );

  function changeRole(value: AccountRole) {
    setActingRole(value);
    setRole(value);
    void queryClient.invalidateQueries({queryKey: ["catalog"]});
  }

  async function logout() {
    await api("/api/v1/auth/logout", {method: "POST", body: "{}"}, session.user);
    location.reload();
  }

  return (
    <main className="portal-page">
      <header className="portal-header">
        <a className="portal-logo" href="/"><img src="/assets/favicon.png" alt="NLRM" /><div><strong>NLRM 综合服务平台</strong><span>NLRM Service Hub</span></div></a>
        <Space size="middle">
          {session.user.role === "admin" && (
            <Segmented<AccountRole>
              value={role}
              onChange={changeRole}
              options={[{label: "常规用户", value: "user"}, {label: "管理员", value: "admin"}]}
            />
          )}
          <div className="portal-user"><Avatar>{session.user.name.slice(0, 1)}</Avatar><div><strong>{session.user.name}</strong><span>{role === "admin" ? "管理员身份" : "常规用户身份"}</span></div></div>
          <Button icon={<LogoutOutlined />} onClick={() => void logout()}>退出</Button>
        </Space>
      </header>
      <section className="portal-hero">
        <div><Tag color="blue">NLRM SERVICE DIRECTORY</Tag><h1>今天要处理什么？</h1><p>从服务目录进入已授权系统；每个系统拥有独立的业务、数据和发布边界。</p></div>
        <div className="hero-metric"><strong>{systems.length}</strong><span>当前可用系统</span></div>
      </section>
      <section className="catalog-toolbar">
        <Input prefix={<SearchOutlined />} value={keyword} onChange={(event) => setKeyword(event.target.value)} allowClear placeholder="搜索系统或业务能力" />
        <Select value={category} onChange={setCategory} options={categories.map((item) => ({label: item, value: item}))} />
      </section>
      {catalog.isLoading ? <div className="catalog-loading"><Spin size="large" /></div> : filtered.length ? (
        <section className="system-grid">
          {filtered.map((item, index) => (
            <Card key={item.id} className="system-card" hoverable onClick={() => {location.href = item.route;}}>
              <div className={`system-icon system-icon-${index % 3}`}>{icons[item.id]}</div>
              <Tag>{item.category}</Tag>
              <Typography.Title level={3}>{item.title}</Typography.Title>
              <Typography.Paragraph type="secondary">{item.description}</Typography.Paragraph>
              <div className="system-card-footer"><span>进入系统</span><span aria-hidden>↗</span></div>
            </Card>
          ))}
        </section>
      ) : <Empty description="没有符合条件的可用系统" />}
      <footer className="portal-footer"><span>NLRM Service Hub · React / FastAPI</span><span>如需切换身份，请返回平台首页操作</span></footer>
    </main>
  );
}

export function PortalApp() {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({queryKey: ["session"], queryFn: loadSession});
  if (sessionQuery.isLoading) return <div className="boot-screen"><Spin size="large" /><span>正在连接 NLRM 综合服务平台</span></div>;
  if (sessionQuery.error) {
    if (sessionQuery.error instanceof ApiError && sessionQuery.error.status !== 401) {
      return <div className="boot-screen"><Alert type="error" showIcon message="平台连接失败" description={sessionQuery.error.message} /></div>;
    }
    return <AuthPage onAuthenticated={(value) => queryClient.setQueryData(["session"], value)} />;
  }
  if (!sessionQuery.data) return <AuthPage onAuthenticated={(value) => queryClient.setQueryData(["session"], value)} />;
  return <Portal session={sessionQuery.data} />;
}
